import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import PublishModal from '../components/PublishModal';

// =====================================================
// ART STUDIO - Digital Canvas for creating artwork
// =====================================================

const BRUSH_TYPES = [
  { id: 'pencil', name: 'Pencil', icon: '✏️', size: 2, opacity: 1 },
  { id: 'pen', name: 'Pen', icon: '🖊️', size: 3, opacity: 1 },
  { id: 'brush', name: 'Brush', icon: '🖌️', size: 8, opacity: 0.8 },
  { id: 'marker', name: 'Marker', icon: '🖍️', size: 12, opacity: 0.6 },
  { id: 'airbrush', name: 'Airbrush', icon: '💨', size: 20, opacity: 0.3 },
  { id: 'eraser', name: 'Eraser', icon: '🧹', size: 15, opacity: 1 },
];

const COLOR_PALETTE = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
  '#FFFF00', '#FF00FF', '#00FFFF', '#FF8800', '#8800FF',
  '#0088FF', '#FF0088', '#88FF00', '#00FF88', '#8800FF',
  '#FF8888', '#88FF88', '#8888FF', '#FFFF88', '#FF88FF',
];

const CANVAS_SIZES = [
  { id: 'square', label: 'Square', width: 1080, height: 1080 },
  { id: 'portrait', label: 'Portrait', width: 1080, height: 1920 },
  { id: 'landscape', label: 'Landscape', width: 1920, height: 1080 },
  { id: 'banner', label: 'Banner', width: 1500, height: 500 },
];

export default function ArtStudio() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  
  // Canvas state
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushType, setBrushType] = useState(BRUSH_TYPES[2]); // Default to brush
  const [brushSize, setBrushSize] = useState(8);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushOpacity, setBrushOpacity] = useState(1);
  const [canvasSize, setCanvasSize] = useState(CANVAS_SIZES[0]);
  const [layers, setLayers] = useState([{ id: 1, name: 'Layer 1', visible: true, locked: false }]);
  const [activeLayer, setActiveLayer] = useState(1);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;
    
    const context = canvas.getContext('2d');
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.lineCap = 'round';
    context.lineJoin = 'round';
    contextRef.current = context;
    
    // Save initial state
    saveToHistory();
  }, [canvasSize]);

  // Save to history
  const saveToHistory = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const imageData = canvas.toDataURL();
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(imageData);
      return newHistory.slice(-50); // Keep last 50 states
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const img = new Image();
      img.src = history[newIndex];
      img.onload = () => {
        contextRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        contextRef.current.drawImage(img, 0, 0);
      };
      setHistoryIndex(newIndex);
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const img = new Image();
      img.src = history[newIndex];
      img.onload = () => {
        contextRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        contextRef.current.drawImage(img, 0, 0);
      };
      setHistoryIndex(newIndex);
    }
  }, [history, historyIndex]);

  // Drawing functions
  const startDrawing = useCallback((e) => {
    const { offsetX, offsetY } = getCoordinates(e);
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  }, []);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    
    const { offsetX, offsetY } = getCoordinates(e);
    const ctx = contextRef.current;
    
    if (brushType.id === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = brushOpacity * brushType.opacity;
    }
    
    ctx.strokeStyle = brushColor;
    ctx.lineWidth = brushSize;
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  }, [isDrawing, brushType, brushColor, brushSize, brushOpacity]);

  const stopDrawing = useCallback(() => {
    if (isDrawing) {
      contextRef.current.closePath();
      setIsDrawing(false);
      saveToHistory();
    }
  }, [isDrawing, saveToHistory]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        offsetX: (e.touches[0].clientX - rect.left) * scaleX,
        offsetY: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      offsetX: (e.clientX - rect.left) * scaleX,
      offsetY: (e.clientY - rect.top) * scaleY,
    };
  };

  // Clear canvas
  const clearCanvas = () => {
    const ctx = contextRef.current;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    saveToHistory();
  };

  // Add tag
  const addTag = () => {
    if (tagInput.trim() && tags.length < 10) {
      setTags([...tags, tagInput.trim().toLowerCase()]);
      setTagInput('');
    }
  };

  // Publish artwork with cross-media support
  const handlePublish = async (publishData, mediaData) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    setSaving(true);
    try {
      const canvas = canvasRef.current;
      const imageBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      
      // Upload the artwork image first
      const formData = new FormData();
      formData.append('file', imageBlob, `${publishData.title}.png`);
      formData.append('title', publishData.title);
      formData.append('description', publishData.description);
      formData.append('art_type', 'digital_painting');
      formData.append('tags', JSON.stringify(publishData.tags));
      formData.append('visibility', publishData.visibility);
      formData.append('width', canvas.width);
      formData.append('height', canvas.height);
      if (publishData.community_id) {
        formData.append('community_id', publishData.community_id);
      }
      formData.append('allow_use_in_videos', 'true');
      
      const response = await apiService.request('/api/art/artworks', {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set content-type for FormData
      });
      
      if (response.artwork) {
        // Also create a post to the art feed if public
        if (publishData.visibility === 'public') {
          try {
            await apiService.createCrossMediaPost({
              caption: publishData.description || publishData.title,
              hashtags: publishData.tags,
              visibility: publishData.visibility,
              content_type: 'image',
              feed_type: 'art',
              source_type: 'art_studio',
              thumbnail_url: response.artwork.image_url,
              artwork_id: response.artwork.id,
              community_id: publishData.community_id,
              linked_content: publishData.linked_content || [],
            });
          } catch (postError) {
            console.warn('Failed to create feed post, but artwork was saved:', postError);
          }
        }
        
        alert('Artwork published successfully!');
        navigate(`/art/${response.artwork.id}`);
      }
    } catch (error) {
      console.error('Failed to publish:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Download artwork
  const downloadArtwork = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `${title || 'artwork'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="h-full bg-slate-950 flex flex-col pt-16 pb-20">
      {/* Toolbar */}
      <div className="flex-none bg-slate-900/80 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-4 flex-wrap">
          {/* Back button */}
          <button
            onClick={() => navigate('/upload')}
            className="p-2 rounded-lg hover:bg-white/10 transition"
          >
            ← Back
          </button>
          
          {/* Canvas size */}
          <select
            value={canvasSize.id}
            onChange={(e) => setCanvasSize(CANVAS_SIZES.find(s => s.id === e.target.value))}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm"
          >
            {CANVAS_SIZES.map(size => (
              <option key={size.id} value={size.id}>{size.label}</option>
            ))}
          </select>
          
          {/* Undo/Redo */}
          <div className="flex gap-1">
            <button
              onClick={undo}
              disabled={historyIndex <= 0}
              className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 transition"
              title="Undo"
            >
              ↩️
            </button>
            <button
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-30 transition"
              title="Redo"
            >
              ↪️
            </button>
          </div>
          
          {/* Clear */}
          <button
            onClick={clearCanvas}
            className="p-2 rounded-lg hover:bg-white/10 transition"
            title="Clear Canvas"
          >
            🗑️
          </button>
          
          <div className="flex-1" />
          
          {/* Actions */}
          <button
            onClick={downloadArtwork}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium transition"
          >
            Download
          </button>
          <button
            onClick={() => setShowPublishModal(true)}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-sm font-bold transition hover:scale-105"
          >
            Publish
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - Tools */}
        <div className="w-20 flex-none bg-slate-900/50 border-r border-white/10 p-2 flex flex-col gap-2">
          <div className="text-xs text-center text-slate-400 mb-2">Tools</div>
          {BRUSH_TYPES.map(brush => (
            <button
              key={brush.id}
              onClick={() => {
                setBrushType(brush);
                setBrushSize(brush.size);
              }}
              className={`p-3 rounded-xl text-xl transition ${
                brushType.id === brush.id
                  ? 'bg-emerald-500/30 border border-emerald-400/50'
                  : 'hover:bg-white/10'
              }`}
              title={brush.name}
            >
              {brush.icon}
            </button>
          ))}
        </div>

        {/* Canvas area */}
        <div className="flex-1 flex items-center justify-center p-4 overflow-auto bg-slate-800/30">
          <canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            className="max-w-full max-h-full border border-white/20 rounded-lg shadow-2xl"
            style={{ cursor: 'crosshair' }}
          />
        </div>

        {/* Right sidebar - Properties */}
        <div className="w-64 flex-none bg-slate-900/50 border-l border-white/10 p-4 overflow-y-auto">
          {/* Brush size */}
          <div className="mb-6">
            <label className="text-xs text-slate-400 block mb-2">Brush Size: {brushSize}px</label>
            <input
              type="range"
              min="1"
              max="100"
              value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value))}
              className="w-full accent-emerald-500"
            />
          </div>
          
          {/* Opacity */}
          <div className="mb-6">
            <label className="text-xs text-slate-400 block mb-2">Opacity: {Math.round(brushOpacity * 100)}%</label>
            <input
              type="range"
              min="0"
              max="100"
              value={brushOpacity * 100}
              onChange={(e) => setBrushOpacity(parseInt(e.target.value) / 100)}
              className="w-full accent-emerald-500"
            />
          </div>
          
          {/* Color picker */}
          <div className="mb-6">
            <label className="text-xs text-slate-400 block mb-2">Color</label>
            <input
              type="color"
              value={brushColor}
              onChange={(e) => setBrushColor(e.target.value)}
              className="w-full h-10 rounded-lg cursor-pointer"
            />
          </div>
          
          {/* Color palette */}
          <div className="mb-6">
            <label className="text-xs text-slate-400 block mb-2">Palette</label>
            <div className="grid grid-cols-5 gap-1">
              {COLOR_PALETTE.map(color => (
                <button
                  key={color}
                  onClick={() => setBrushColor(color)}
                  className={`w-8 h-8 rounded-lg border-2 transition ${
                    brushColor === color ? 'border-white scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          
          {/* Layers */}
          <div>
            <label className="text-xs text-slate-400 block mb-2">Layers</label>
            <div className="space-y-1">
              {layers.map(layer => (
                <div
                  key={layer.id}
                  onClick={() => setActiveLayer(layer.id)}
                  className={`p-2 rounded-lg text-sm flex items-center gap-2 cursor-pointer ${
                    activeLayer === layer.id
                      ? 'bg-emerald-500/30 border border-emerald-400/50'
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <span className="text-lg">{layer.visible ? '👁️' : '🙈'}</span>
                  <span className="flex-1 truncate">{layer.name}</span>
                  {layer.locked && <span>🔒</span>}
                </div>
              ))}
            </div>
            <button
              onClick={() => setLayers([...layers, { id: Date.now(), name: `Layer ${layers.length + 1}`, visible: true, locked: false }])}
              className="w-full mt-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-slate-400"
            >
              + Add Layer
            </button>
          </div>
        </div>
      </div>

      {/* Publish Modal */}
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onPublish={handlePublish}
        contentType="art"
        initialTitle={title}
        initialDescription={description}
        initialTags={tags}
      />
    </div>
  );
}
